import React, { useState, useEffect, useCallback } from "react";
import {
  Collapse,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Popconfirm,
  Spin,
  message,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  _get_all_faqs_admin,
  _create_faq,
  _update_faq,
  _delete_faq,
} from "../../../common/axios_services";

const { TextArea } = Input;

const FAQ_PAGES = [
  { value: "HOME_TOOL", label: "Home" },
  { value: "MARKETPLACE", label: "Marketplace" },
  { value: "SERVICE", label: "Service" },
  { value: "CHECKOUT", label: "Checkout" },
  { value: "MY_ORDER", label: "My Order" },
  { value: "WALLET", label: "Wallet" },
  { value: "REFERRAL", label: "Referral" },
  { value: "RESTAURANT_DASHBOARD", label: "Restaurant Dashboard" },
  { value: "DRIVER_HOME", label: "Driver Home" },
];

const AdminFaqs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterPage, setFilterPage] = useState(null);
  const [form] = Form.useForm();

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await _get_all_faqs_admin();
      setFaqs(response?.data?.data?.data || response?.data?.data || []);
    } catch (error) {
      message.error("Failed to fetch FAQs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const openCreateModal = () => {
    setEditingFaq(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalOpen(true);
  };

  const openEditModal = (faq) => {
    setEditingFaq(faq);
    form.setFieldsValue({
      question: faq.question,
      answer: faq.answer,
      page: faq.page,
      isActive: faq.isActive,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingFaq) {
        await _update_faq(editingFaq.id, values);
        message.success("FAQ updated");
      } else {
        await _create_faq(values);
        message.success("FAQ created");
      }

      setModalOpen(false);
      form.resetFields();
      setEditingFaq(null);
      fetchFaqs();
    } catch (error) {
      if (error?.errorFields) return;
      message.error("Failed to save FAQ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await _delete_faq(id);
      message.success("FAQ deleted");
      fetchFaqs();
    } catch (error) {
      message.error("Failed to delete FAQ");
    }
  };

  const filteredFaqs = filterPage
    ? faqs.filter((f) => f.page === filterPage)
    : faqs;

  const pageLabel = (page) =>
    FAQ_PAGES.find((p) => p.value === page)?.label || page;

  const pageColor = {
    HOME_TOOL: "blue",
    MARKETPLACE: "purple",
    SERVICE: "cyan",
    CHECKOUT: "orange",
    MY_ORDER: "geekblue",
    WALLET: "green",
    REFERRAL: "magenta",
    RESTAURANT_DASHBOARD: "volcano",
    DRIVER_HOME: "gold",
  };

  const renderCollapseItems = () => {
    return filteredFaqs.map((faq, idx) => ({
      key: faq.id || idx,
      label: (
        <div className="flex justify-between items-center w-full pr-4">
          <span className="font-semibold truncate">{faq.question}</span>
          <Tag color={pageColor[faq.page] || "default"} className="ml-2">
            {pageLabel(faq.page)}
          </Tag>
        </div>
      ),
      children: (
        <div>
          <ul className="mobile-list-flex">
            <li>
              <span>Question:</span>
              <span>{faq.question}</span>
            </li>
            <li className="flex-col !items-start">
              <span>Answer:</span>
              <div className="mt-1 text-gray-600 w-full">{faq.answer}</div>
            </li>
            <li>
              <span>Category/Page:</span>
              <Tag color={pageColor[faq.page] || "default"}>
                {pageLabel(faq.page)}
              </Tag>
            </li>
            <li>
              <span>Status:</span>
              <Tag color={faq.isActive ? "green" : "red"}>
                {faq.isActive ? "Active" : "Inactive"}
              </Tag>
            </li>
            <li className="mt-4 !border-b-0">
              <span>Actions:</span>
              <div className="flex gap-2">
                <Button
                  size="small"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(faq)}
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Delete this FAQ?"
                  onConfirm={() => handleDelete(faq.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    Delete
                  </Button>
                </Popconfirm>
              </div>
            </li>
          </ul>
        </div>
      ),
    }));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-gilroyBold text-lg m-0">FAQ Management</h3>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
          size="small"
        >
          Add FAQ
        </Button>
      </div>

      <div className="mb-4">
        <Select
          allowClear
          placeholder="Filter by page"
          value={filterPage}
          onChange={setFilterPage}
          options={FAQ_PAGES}
          className="w-full"
        />
      </div>

      <Spin spinning={loading} indicator={antIcon}>
        <div className="mobile topup-data">
          {filteredFaqs.length === 0 ? (
            <Empty description="No FAQs found" />
          ) : (
            <Collapse
              items={renderCollapseItems()}
              defaultActiveKey={[]}
              expandIconPosition="end"
            />
          )}
        </div>
      </Spin>

      <Modal
        title={editingFaq ? "Edit FAQ" : "Create FAQ"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setEditingFaq(null);
        }}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editingFaq ? "Update" : "Create"}
        width={600}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="question"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="e.g. How do I fund my wallet?" />
          </Form.Item>

          <Form.Item
            name="answer"
            label="Answer"
            rules={[{ required: true, message: "Please enter an answer" }]}
          >
            <TextArea rows={4} placeholder="Enter the answer..." />
          </Form.Item>

          <Form.Item
            name="page"
            label="Page"
            rules={[{ required: true, message: "Please select a page" }]}
          >
            <Select options={FAQ_PAGES} placeholder="Select page" />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminFaqs;
